import React from "react";
import { Button, Dropdown, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useFetcher } from "react-router-dom";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import type { ReadTransactionReceiptResult } from "~/types/paymentReceipt";
import { INTENTS } from "~/utils";

export type ViewReceiptButtonProps = {
  transactionID: string;
};

type ReceiptActionFailure = {
  success: false;
  error: string;
};

const RECEIPT_LANGUAGE_OPTIONS = [
  { key: "eng", label: "English" },
  { key: "fr", label: "Français" },
  { key: "es", label: "Español" },
] as const;

function normalizeUiLanguage(lng: string | undefined): string {
  const code = (lng ?? "eng").split("-")[0];
  if (code === "eng" || code === "fr" || code === "es") return code;
  return "eng";
}

function isReceiptActionFailure(data: unknown): data is ReceiptActionFailure {
  return (
    typeof data === "object" &&
    data != null &&
    "success" in data &&
    (data as ReceiptActionFailure).success === false
  );
}

export function ViewReceiptButton({ transactionID }: ViewReceiptButtonProps) {
  const { t, i18n } = useAppTranslation();
  const fetcher = useFetcher({ key: `transaction-receipt-${transactionID}` });
  const lastHandledKeyRef = React.useRef<string | null>(null);

  const isLoading = fetcher.state !== "idle";

  const handleViewReceipt = React.useCallback(
    (language: string) => {
      fetcher.submit(
        {
          intent: INTENTS._view_transaction_receipt,
          transactionID,
          language: normalizeUiLanguage(language),
        },
        { method: "POST" },
      );
    },
    [fetcher, transactionID],
  );

  React.useEffect(() => {
    if (fetcher.state !== "idle" || fetcher.data == null) {
      return;
    }

    const actionKey = `${transactionID}:${fetcher.state}:${JSON.stringify(fetcher.data)}`;
    if (lastHandledKeyRef.current === actionKey) {
      return;
    }
    lastHandledKeyRef.current = actionKey;

    if (isReceiptActionFailure(fetcher.data)) {
      message.error(fetcher.data.error ?? t("Sorry_something_went_wrong"));
      return;
    }

    const data = fetcher.data as ReadTransactionReceiptResult;

    if (data.status === "ready" && data.downloadUrl) {
      window.open(data.downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    message.info(t("Receipt_not_available"));
  }, [fetcher.data, fetcher.state, t, transactionID]);

  return (
    <Dropdown.Button
      type="link"
      size="small"
      loading={isLoading}
      onClick={() => handleViewReceipt(normalizeUiLanguage(i18n.language))}
      icon={<DownOutlined />}
      menu={{
        items: RECEIPT_LANGUAGE_OPTIONS.map((opt) => ({
          key: opt.key,
          label: opt.label,
        })),
        onClick: ({ key }) => handleViewReceipt(String(key)),
      }}
    >
      {t("View_receipt")}
    </Dropdown.Button>
  );
}
